<template>
  <div class="container-fluid">
    <div class="row justify-content-center align-items-center">
      <div class="col-md-12 col-sm-12 col-lg-12 mt-5">
        <div class="card main-card">
          <div class="card-body">
            <h5>Heat Index</h5>
            <p class="mb-3">Run the hIsms script manually from the interface. Check the logs table below for execution results.</p>
            <button class="btn btn-primary" :disabled="runHeatIndexLoading" @click="runHeatIndex">
              <span v-if="runHeatIndexLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <span class="text-white" v-if="runHeatIndexLoading">Running...</span>
              <span class="text-white" v-else>Run Heat Index</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="row justify-content-center align-items-center">
      <div class="col-md-12 col-sm-12 col-lg-12 mt-5">
        <div class="card main-card">
          <div class="card-body">
            <h5>Logs</h5>
            
            <!-- Filters Section -->
            <div class="filters-section mt-4 p-3 border rounded bg-light">
              <h6 class="mb-3">Filters</h6>
              <div class="row g-3">
                <!-- User Filter -->
                <div class="col-md-3">
                  <label class="form-label">User</label>
                  <input type="text" class="form-control" v-model="filters.user" placeholder="Search by user..." @keyup.enter="applyFilters">
                </div>
                
                <!-- Action Filter -->
                <div class="col-md-3">
                  <label class="form-label">Action</label>
                  <input type="text" class="form-control" v-model="filters.action" placeholder="e.g., INSERT, UPDATE" @keyup.enter="applyFilters">
                </div>
                
                <!-- Affected Table Filter -->
                <div class="col-md-3">
                  <label class="form-label">Affected Table</label>
                  <input type="text" class="form-control" v-model="filters.affectedTable" placeholder="Table name..." @keyup.enter="applyFilters">
                </div>
                
                <!-- Search in Details -->
                <div class="col-md-3">
                  <label class="form-label">Search in Details</label>
                  <input type="text" class="form-control" v-model="filters.search" placeholder="Search details..." @keyup.enter="applyFilters">
                </div>
                
                <!-- Start Date -->
                <div class="col-md-3">
                  <label class="form-label">Start Date</label>
                  <input type="datetime-local" class="form-control" v-model="filters.startDate" @change="applyFilters">
                </div>
                
                <!-- End Date -->
                <div class="col-md-3">
                  <label class="form-label">End Date</label>
                  <input type="datetime-local" class="form-control" v-model="filters.endDate" @change="applyFilters">
                </div>
                
                <!-- Sort By -->
                <div class="col-md-2">
                  <label class="form-label">Sort By</label>
                  <select class="form-select" v-model="filters.sortBy" @change="applyFilters">
                    <option value="date">Date</option>
                    <option value="id">ID</option>
                    <option value="user">User</option>
                    <option value="action">Action</option>
                    <option value="affected_table">Affected Table</option>
                  </select>
                </div>
                
                <!-- Sort Order -->
                <div class="col-md-2">
                  <label class="form-label">Order</label>
                  <select class="form-select" v-model="filters.sortOrder" @change="applyFilters">
                    <option value="DESC">Descending</option>
                    <option value="ASC">Ascending</option>
                  </select>
                </div>
                
                <!-- Per Page -->
                <div class="col-md-2">
                  <label class="form-label">Per Page</label>
                  <select class="form-select" v-model.number="filters.perPage" @change="applyFilters">
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                </div>
                
                <!-- Action Buttons -->
                <div class="col-12 d-flex gap-2">
                  <button class="btn btn-primary" @click="applyFilters" :disabled="logsLoading">
                    <span v-if="logsLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Search
                  </button>
                  <button class="btn btn-outline-secondary" @click="clearFilters" :disabled="logsLoading">
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
            
            <div v-if="logsLoading" class="text-center my-4">
              <span>Loading logs...</span>
            </div>
            <div v-else>
              <div v-if="logs && logs.length" class="mt-3">
                <div class="d-flex justify-content-end mt-4">
                  <v-pagination v-model.number="logsCurrentPage" :page-count="logsTotalPages" :click-handler="fetchLogs"
                    :prev-text="'Previous'" :next-text="'Next'" :container-class="'pagination'" @input="fetchLogs" />
                </div>
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <p>Total records: {{ totalRecords }} (Showing {{ logs.length }} on this page)</p>
                  </div>
                  <div>
                    <p>Page {{ logsCurrentPage }} of {{ logsTotalPages }}</p>
                  </div>
                </div>
                <div class="table-responsive">
                  <vs-table>
                    <template #thead>
                      <vs-tr>
                        <vs-th>ID</vs-th>
                        <vs-th>User</vs-th>
                        <vs-th>Action</vs-th>
                        <vs-th>Affected Table</vs-th>
                        <vs-th>Date</vs-th>
                        <vs-th style="width: 333px;">Details</vs-th>
                      </vs-tr>
                    </template>
                    <template #tbody>
                      <vs-tr v-for="log in logs" :key="log.id">
                        <vs-td>{{ log.id }}</vs-td>
                        <vs-td>{{ log.user }}</vs-td>
                        <vs-td>{{ log.action }}</vs-td>
                        <vs-td>{{ log.affected_table }}</vs-td>
                        <vs-td>{{ new Date(log.date).toLocaleString() }}</vs-td>
                        <vs-td>{{ log.details }}</vs-td>
                      </vs-tr>
                    </template>
                  </vs-table>
                </div>
                <div class="d-flex justify-content-center mt-4">
                  <v-pagination v-model.number="logsCurrentPage" :page-count="logsTotalPages" :click-handler="fetchLogs"
                    :prev-text="'Previous'" :next-text="'Next'" :container-class="'pagination'" @input="fetchLogs" />
                </div>
              </div>
              <div v-else class="text-center my-4">
                <span>No logs to display.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'
  import vPagination from 'vue-plain-pagination'
  var config = require("@/config/api.json");
  const API = config.api;

  export default {
    name: 'Logs',
    components: { vPagination },
    data() {
      return {
        logs: [],
        logsTotalPages: 1,
        logsCurrentPage: 1,
        logsLoading: false,
        totalRecords: 0,
        runHeatIndexLoading: false,
        filters: {
          user: '',
          action: '',
          affectedTable: '',
          startDate: '',
          endDate: '',
          search: '',
          sortBy: 'date',
          sortOrder: 'DESC',
          perPage: 25
        }
      }
    },
    mounted() {
      this.fetchLogs(1);
    },
    methods: {
      async fetchLogs(page = 1) {
        this.logsLoading = true;
        try {
          // Build query parameters
          const params = {
            page: page,
            perPage: this.filters.perPage,
            sortBy: this.filters.sortBy,
            sortOrder: this.filters.sortOrder
          };

          // Add filters only if they have values
          if (this.filters.user) params.user = this.filters.user;
          if (this.filters.action) params.action = this.filters.action;
          if (this.filters.affectedTable) params.affectedTable = this.filters.affectedTable;
          if (this.filters.search) params.search = this.filters.search;
          
          // Format dates for API (convert from datetime-local to MySQL format)
          if (this.filters.startDate) {
            params.startDate = this.filters.startDate.replace('T', ' ');
          }
          if (this.filters.endDate) {
            params.endDate = this.filters.endDate.replace('T', ' ');
          }

          const res = await axios.get(`${API}/getLogsDetailed`, { params });
          
          if (res.data && res.data.status === 'success') {
            this.logs = res.data.data;
            this.logsTotalPages = res.data.pagination.totalPages || 1;
            this.logsCurrentPage = res.data.pagination.currentPage || 1;
            this.totalRecords = res.data.pagination.totalRecords || 0;
          } else {
            this.logs = [];
            this.logsTotalPages = 1;
            this.totalRecords = 0;
          }
        } catch (e) {
          console.error('Error fetching logs:', e);
          this.logs = [];
          this.logsTotalPages = 1;
          this.totalRecords = 0;
        } finally {
          this.logsLoading = false;
        }
      },
      applyFilters() {
        // Reset to page 1 when applying filters
        this.logsCurrentPage = 1;
        this.fetchLogs(1);
      },
      clearFilters() {
        // Reset all filters to default values
        this.filters = {
          user: '',
          action: '',
          affectedTable: '',
          startDate: '',
          endDate: '',
          search: '',
          sortBy: 'date',
          sortOrder: 'DESC',
          perPage: 25
        };
        this.logsCurrentPage = 1;
        this.fetchLogs(1);
      },
      async runHeatIndex() {
        this.runHeatIndexLoading = true;
        
        try {
          const res = await axios.get(`${API}/run-heatIndex`);
          
          if (res.data && res.data.success) {
            // Show success notification
            
            setTimeout(() => {
              this.notify('success', 'Heat Index script started successfully. Check the logs table below for results.');
            }, 10000);
            
            // Refresh logs after a short delay to show the new execution log
            setTimeout(() => {
              this.fetchLogs(this.logsCurrentPage);
            }, 1000);
          } else {
            this.notify('error', 'Unexpected response from server.');
          }
        } catch (e) {
          console.error('Error running heat index:', e);
          
          let errorMessage = 'Failed to start Heat Index script.';
          
          if (e.response) {
            // Server responded with an error
            errorMessage = e.response.data?.message || e.response.data?.error || errorMessage;
          } else if (e.request) {
            // Request was made but no response received
            errorMessage = 'No response from server. Please check your connection.';
          } else {
            // Error in request configuration
            errorMessage = e.message || errorMessage;
          }
          
          this.notify('error', errorMessage);
        } finally {
          setTimeout(() => {
            this.runHeatIndexLoading = false;
          }, 10000);
        }
      }
    }
  }
</script>

<style>
  /* You can add custom styles here if needed */
  .log-output {
    background-color: #1f1f1f;
    color: #ffffff;
    padding: 1rem;
    border-radius: 0.5rem;
    max-height: 300px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .filters-section {
    background-color: #f8f9fa !important;
  }

  .filters-section h6 {
    color: #495057;
    font-weight: 600;
  }

  .form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.25rem;
  }
</style>